/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */

export interface Base64File {
  name: string;
  type: string;
  size: number;
  base64: string;
}

const MAX_SIDE = 1600;

// Stesso limite di `ReceiptValidator::MAX_BYTES` sul server: controllarlo qui
// evita di spedire un PDF enorme per sentirsi rispondere 422.
const MAX_BYTES = 5242880;

// Il validatore `Base64Attachment` accetta nomi lunghi al più 255 caratteri.
const MAX_NAME_LENGTH = 255;

function readAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

// Il validatore `Base64Attachment` confronta `size` con i byte decodificati:
// quattro caratteri base64 valgono tre byte, meno il padding finale.
function toBase64File(name: string, type: string, dataUrl: string): Base64File {
  const base64 = dataUrl.substring(dataUrl.indexOf(',') + 1);
  const padding = (base64.match(/=+$/) || [''])[0].length;
  return {name, type, base64, size: (base64.length * 3) / 4 - padding};
}

// Nome con l'estensione voluta e nei limiti del validatore; "ricevuta" se il
// telefono non ne fornisce uno utilizzabile.
function fileName(original: string, extension: string): string {
  const stem = original.replace(/\.[^.]*$/, '').trim() || 'ricevuta';
  return (
    stem.substring(0, MAX_NAME_LENGTH - extension.length - 1) + '.' + extension
  );
}

export function isPdf(file: File): boolean {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
}

function loadImage(file: File): Promise<HTMLImageElement> {
  // Un object URL invece di un data URL: una foto da 10 MB non passa per
  // una stringa base64 da 13 MB solo per essere decodificata.
  const url = URL.createObjectURL(file);
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(
        new Error(
          'Non riesco a leggere questa immagine: scatta di nuovo la foto o allega un PDF.',
        ),
      );
    };
    img.src = url;
  });
}

/**
 * Le foto si riducono a JPEG con lato massimo 1600 px (una foto da telefono
 * pesa 3-10 MB, ridotta sta sotto il mezzo mega ed è ancora leggibile); i
 * PDF passano invariati. Rifiuta con un `Error` dal messaggio italiano,
 * pronto da mostrare.
 */
export async function prepareReceipt(file: File): Promise<Base64File> {
  if (isPdf(file)) {
    if (file.size > MAX_BYTES) {
      throw new Error('Il PDF supera i 5 MB: allegane uno più leggero.');
    }
    return toBase64File(
      fileName(file.name, 'pdf'),
      'application/pdf',
      await readAsDataUrl(file),
    );
  }
  if (file.type !== '' && !file.type.startsWith('image/')) {
    throw new Error('La ricevuta deve essere una foto oppure un PDF.');
  }

  const img = await loadImage(file);
  const scale = Math.min(
    1,
    MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight),
  );
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Il telefono non riesce a ridurre la foto: riprova.');
  }
  // Sfondo bianco: un PNG trasparente convertito in JPEG diventerebbe nero.
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(img, 0, 0, canvas.width, canvas.height);

  const result = toBase64File(
    fileName(file.name, 'jpg'),
    'image/jpeg',
    canvas.toDataURL('image/jpeg', 0.8),
  );
  if (result.size === 0) {
    throw new Error('Il telefono non riesce a ridurre la foto: riprova.');
  }
  return result;
}
